
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Cpu,
  Filter,
  ExternalLink,
  Zap,
  Clock,
  Upload,
  X
} from 'lucide-react';
import { fetchWorkflows, upsertWorkflow } from '../../adk-agent-builder/api';
import { Workspace } from '../types';
import { useAuth } from '../../context/AuthContext';
import { apiUrl } from '../../utils/api';

interface SavedWorkflow {
  id: string;
  name: string;
  description?: string;
  lastModified: number;
  deployed?: boolean;
  identifier?: string;
  workspace?: string;
}

interface AgentsWorkflowsProps {
  selectedWorkspace?: Workspace | null;
  onResetFilter?: () => void;
  onUpdateWorkspace?: (ws: Workspace) => void;
}

const AgentsWorkflows: React.FC<AgentsWorkflowsProps> = ({ selectedWorkspace, onResetFilter, onUpdateWorkspace }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [savedWorkflows, setSavedWorkflows] = useState<SavedWorkflow[]>([]);

  // Load saved workflows from backend
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        const workflows = await fetchWorkflows();
        if (isMounted) {
          setSavedWorkflows(workflows);
        }
      } catch {
        if (isMounted) {
          setSavedWorkflows([]);
        }
      }
    };
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Check if a workflow is deployed
  const isDeployed = (workflow: SavedWorkflow) => {
    return Boolean(workflow.deployed);
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toISOString().split('T')[0];
  };

  // Handle JSON import
  const handleImportJSON = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const importedWorkflow = JSON.parse(event.target?.result as string);
        // Add unique id and timestamp
        const newWorkflow = {
          ...importedWorkflow,
          id: importedWorkflow.id || `imported_${Date.now()}`,
          lastModified: Date.now(),
          deployed: false,
        };
        const persisted = await upsertWorkflow({
          ...newWorkflow,
          nodes: importedWorkflow.nodes || [],
          connections: importedWorkflow.connections || [],
        });
        setSavedWorkflows(prev => {
          const updated = prev.filter(w => w.id !== persisted.id);
          updated.push(persisted);
          return updated;
        });
        alert(`Workflow "${persisted.name}" imported successfully!`);
      } catch (err) {
        alert('Invalid JSON file. Please check the format.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Handle agent selection/toggle
  const handleAgentToggle = (agentId: string) => {
    if (!selectedWorkspace || !onUpdateWorkspace) return;

    const currentAgentIds = selectedWorkspace.agentIds || [];
    const isSelected = currentAgentIds.includes(agentId);

    let newAgentIds;
    if (isSelected) {
      newAgentIds = currentAgentIds.filter(id => id !== agentId);
    } else {
      newAgentIds = [...currentAgentIds, agentId];
    }

    onUpdateWorkspace({
      ...selectedWorkspace,
      agentIds: newAgentIds
    });
  };

  const { user, token } = useAuth();
  const [userWorkspaces, setUserWorkspaces] = useState<Workspace[]>([]);

  // Fetch workspaces to filter workflows
  useEffect(() => {
    let isMounted = true;
    const loadWorkspaces = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/workspaces/', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok && isMounted) {
          const data = await response.json();
          setUserWorkspaces(data);
        }
      } catch (err) {
        console.error('Failed to fetch workspaces in AgentsWorkflows', err);
      }
    };
    loadWorkspaces();
    return () => { isMounted = false; };
  }, [token]);

  // Determine which workflows the user can see
  // A workflow is visible if:
  // 1. User is Super Admin or Org Admin
  // 2. OR User is Workspace Admin (global)
  // 3. OR the workflow is assigned to a workspace where the user is a member (Admin or Viewer)
  // 4. OR the workflow is not assigned to any workspace yet
  const filteredWorkflows = savedWorkflows.filter(workflow => {
    if (user?.role === 'Super Admin' || user?.role === 'Org Admin' || user?.role === 'Workspace Admin' || user?.role === 'Builder') {
      return true;
    }

    // Check if the workflow's ID is in any of the workspaces the user has access to
    const isAssignedToUserWorkspace = userWorkspaces.some(ws => {
      const isMember = ws.members?.some(m => m.userId === user?.id) || ws.owner === user?.id; // owner or member
      return isMember && ws.agentIds?.includes(workflow.id);
    });

    // Also consider workflows that are not explicitly assigned to any workspace
    // depending on the platform's exact visibility rules.
    // Assuming unassigned workflows are visible or managed differently.
    // If we only want assigned ones:
    return isAssignedToUserWorkspace;
  });

  // Only show saved workflows (no mock agents)
  const allAgents = filteredWorkflows.map(w => ({
    id: w.id,
    name: w.name.replace(/_/g, ' '),
    type: 'Workflow' as const,
    workspace: w.workspace || 'Custom',
    status: isDeployed(w) ? 'Published' : 'Draft',
    lastUpdated: formatDate(w.lastModified),
    owner: 'You',
    workflowId: w.id,
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Hidden file input for JSON import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".json"
        className="hidden"
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Agents & Workflows</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage individual agent lifecycle and complex multi-agent flows.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleImportJSON}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-all"
            title="Import a workflow from a JSON file exported from Agent Builder"
          >
            <Upload size={16} /> Import JSON
          </button>
          <button
            onClick={() => navigate('/admin/agent-builder')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
          >
            <Zap size={18} /> New Agent
          </button>
        </div>
      </div>

      {selectedWorkspace && (
        <div className="mb-6 animate-fadeIn">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 dark:bg-blue-500 rounded-lg text-white">
                <Cpu size={18} />
              </div>
              <div>
                <p className="text-sm font-bold text-blue-900">Workspace Selection Mode</p>
                <p className="text-xs text-blue-700">Select agents below to add them to <span className="font-bold underline">{selectedWorkspace.name}</span></p>
              </div>
            </div>
            <button
              onClick={onResetFilter}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-200 transition-colors"
            >
              <X size={14} /> Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Catalog Search & Filter */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm mb-6 transition-colors">
        <div className="relative max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
          <input
            type="text"
            placeholder="Filter agents by name, tag or owner..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 dark:text-white transition-colors"
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <select className="flex-1 md:flex-none text-xs font-bold bg-slate-100 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 outline-none border-none transition-colors">
            <option>All Types</option>
            <option>Chat</option>
            <option>Orchestrator</option>
            <option>Workflow</option>
          </select>
          <select className="flex-1 md:flex-none text-xs font-bold bg-slate-100 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 outline-none border-none transition-colors">
            <option>All Environments</option>
            <option>Production</option>
            <option>UAT</option>
            <option>Development</option>
          </select>
          <button className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-all">
            <Filter size={18} />
          </button>
        </div>
      </div>

      {/* Agents Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
        <div className="max-h-[300px] overflow-y-auto">
          <table className="w-full text-left">
            <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900/50 backdrop-blur-sm">
              <tr className="text-slate-500 dark:text-slate-400 uppercase text-[10px] font-bold tracking-wider">
                {selectedWorkspace && <th className="px-6 py-3 w-10">Select</th>}
                <th className="px-6 py-3">Agent Name</th>
                <th className="px-6 py-3">Workspace</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Environment</th>
                <th className="px-6 py-3">Last Updated</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {allAgents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-400 dark:text-slate-500 text-sm">
                    {selectedWorkspace
                      ? `No agents found in ${selectedWorkspace.name}.`
                      : 'No workflows yet. Click "New Agent" or "Import JSON" to get started.'
                    }
                  </td>
                </tr>
              ) : (
                allAgents.map((agent: any) => (
                  <tr key={agent.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
                    {selectedWorkspace && (
                      <td className="px-6 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedWorkspace.agentIds?.includes(agent.id) || false}
                          onChange={() => handleAgentToggle(agent.id)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                          <Cpu size={18} />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">{agent.name}</p>
                          <p className="text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 mt-0.5">{agent.type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{agent.workspace}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${agent.status === 'Published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                        {agent.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-medium text-slate-500">Development</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-slate-400">
                        <Clock size={14} />
                        <span className="text-xs">{agent.lastUpdated}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => navigate(`/admin/agent-builder?workflow=${agent.workflowId}`)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Open in Agent Builder"
                      >
                        <ExternalLink size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Workflow Visualization Section */}
      <div className="mt-6 p-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-3xl flex flex-col items-center justify-center text-center flex-shrink-0 transition-colors">
        <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/20 text-blue-400 dark:text-blue-500 rounded-full flex items-center justify-center mb-3">
          <Zap size={28} />
        </div>
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">Build Complex Workflows</h3>
        <p className="text-xs text-slate-500 max-w-sm mb-4">Use the EngazeIQ Builder to connect multiple agents, external tools, and conditional logic nodes into production-ready flows.</p>
        <button className="px-6 py-2 bg-slate-900 text-white rounded-2xl text-xs font-bold shadow-lg shadow-slate-900/20 hover:scale-105 transition-all" onClick={() => navigate('/admin/agent-builder')}>
          Launch Workflow Builder
        </button>
      </div>
    </div>
  );
};

export default AgentsWorkflows;
